import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Button, Card, Form, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import ApiCaller from '../../api/ApiCaller';
import { GET_CONFIG_DATA_TYPES } from '../../api/ApiConst';
import DataTypeFactory from './DataTypeFactory.jsx';


// Function to get data types via API call and return dropdown options
export const getDataTypes = async () => {
    try {
        const response = await new Promise((resolve, reject) => {
            ApiCaller.callGetApi(
                GET_CONFIG_DATA_TYPES,
                {},
                (response) => {
                    resolve(response || {});
                },
                (error) => {
                    console.error("getDataTypes: Error fetching config data types:", error);
                    reject(error);
                }
            );
        });

        // Convert API response to dropdown options
        if (response && typeof response === 'object') {
            const options = Object.keys(response).map(type => ({
                label: type,
                value: type
            }));
            return options;
        }

        return [];
    } catch (error) {
        console.error("getDataTypes: Error in getDataTypes:", error);
        return [];
    }
};

// Function to get configuration fields for a specific data type
export const getDataTypeConfig = async (dataType) => {
    try {
        const response = await new Promise((resolve, reject) => {
            ApiCaller.callGetApi(
                GET_CONFIG_DATA_TYPES,
                {},
                (response) => {
                    resolve(response || {});
                },
                (error) => {
                    console.error("getDataTypeConfig: Error fetching config data types:", error);
                    reject(error);
                }
            );
        });

        if (response && response[dataType]) {
            return response[dataType];
        }

        return [];
    } catch (error) {
        console.error("getDataTypeConfig: Error in getDataTypeConfig:", error);
        return [];
    }
};

// Component that renders configuration fields based on selected data type
export const DataTypeConfigView = ({
    selectedDataType,
    configData,
    onConfigChange,
    referenceTableOptions = [],
    keyValueAttributeOptions = [],
    mappingTableAttributeKeyValueOptions = [],
    dynamicKeyValueAttributeOptions = [],
    setDynamicKeyValueAttributeOptions,
    hasDynamicOptions = false,
    setHasDynamicOptions
}) => {
    const [configFields, setConfigFields] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [objectAttributes, setObjectAttributes] = useState([]);
    const [enumValues, setEnumValues] = useState([]);
    const [referenceTableKeyValue, setReferenceTableKeyValue] = useState("");
    const [keyAttributeKeyValue, setKeyAttributeKeyValue] = useState("");
    const [valueAttributeKeyValue, setValueAttributeKeyValue] = useState("");
    const [foreignKeyKeyValue, setForeignKeyKeyValue] = useState("");
    const [dynamicMappingTableAttributeOptions, setDynamicMappingTableAttributeOptions] = useState([]);
    // Removed forceRender to avoid forced remounts that cause flickering
    const [hasDynamicMappingOptions, setHasDynamicMappingOptions] = useState(false);
    const dynamicOptionsRef = useRef([]);
    const hasDynamicOptionsRef = useRef(false);
    const lastSelectedDataType = useRef(null);

    // Track component mounting
    useEffect(() => {
        console.log('DataTypeConfigView component mounted');
        return () => {
            console.log('DataTypeConfigView component unmounted');
        };
    }, []);

    // Memoize the loadConfigFields function to prevent unnecessary re-renders
    const loadConfigFields = useCallback(async (dataType) => {
        // Prevent duplicate calls for the same data type
        if (lastSelectedDataType.current === dataType) {
            console.log('Skipping duplicate API call for:', dataType);
            return;
        }
        lastSelectedDataType.current = dataType;
        
        console.log('Making API call for data type:', dataType);
        setIsLoading(true);
        try {
            const fields = await getDataTypeConfig(dataType);
            setConfigFields(fields);
        } catch (error) {
            console.error('Error loading config fields:', error);
            setConfigFields([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        console.log('useEffect triggered with dependencies:', {
            selectedDataType: selectedDataType?.value,
            referenceTableOptionsLength: referenceTableOptions.length,
            keyValueAttributeOptionsLength: keyValueAttributeOptions.length,
            configDataKeys: configData ? Object.keys(configData) : []
        });
        
        if (selectedDataType && selectedDataType.value) {
            console.log('Processing data type:', selectedDataType.value);
            
            if (selectedDataType.value === 'OBJECT') {
                // For OBJECT type, we don't need to fetch config fields
                console.log('OBJECT type - setting empty config fields');
                console.log('OBJECT type - configData?.attributes:', configData?.attributes);
                setConfigFields([]);
                setObjectAttributes(configData?.attributes || []);
            } else if (selectedDataType.value === 'ENUM') {
                // For ENUM type, we don't need to fetch config fields
                console.log('ENUM type - setting empty config fields');
                setConfigFields([]);
                setEnumValues(configData?.enumValues || []);
            } else if (selectedDataType.value === 'COLLECTION_OBJECT') {
                // For COLLECTION_OBJECT type, we don't need to fetch config fields
                console.log('COLLECTION_OBJECT type - setting empty config fields');
                setConfigFields([]);
                console.log('COLLECTION_OBJECT configData:', configData);

                // Initialize reference table values from configData
                if (configData?.referenceTable) {
                    const refTable = referenceTableOptions.find(opt => opt.value === configData.referenceTable);
                    console.log('Setting referenceTableKeyValue:', refTable);
                    setReferenceTableKeyValue(refTable || "");
                }
                if (configData?.keyAttribute) {
                    const keyAttr = keyValueAttributeOptions.find(opt => opt.value === configData.keyAttribute);
                    console.log('Setting keyAttributeKeyValue:', keyAttr);
                    setKeyAttributeKeyValue(keyAttr || "");
                }
                if (configData?.valueAttribute) {
                    const valueAttr = keyValueAttributeOptions.find(opt => opt.value === configData.valueAttribute);
                    console.log('Setting valueAttributeKeyValue:', valueAttr);
                    setValueAttributeKeyValue(valueAttr || "");
                }
                if (configData?.foreignKey) {
                    const foreignKey = keyValueAttributeOptions.find(opt => opt.value === configData.foreignKey);
                    console.log('Setting foreignKeyKeyValue:', foreignKey);
                    setForeignKeyKeyValue(foreignKey || "");
                }
            } else {
                // Call loadConfigFields for all types - let the API handle configuration
                console.log('Calling loadConfigFields for:', selectedDataType.value);
                loadConfigFields(selectedDataType.value);
            }
        }
    }, [selectedDataType?.value]); // Removed loadConfigFields from dependencies

    // Handle configData changes for OBJECT and ENUM types without causing infinite loops
    useEffect(() => {
        console.log('DataTypeConfigView - configData changed:', configData);
        console.log('DataTypeConfigView - selectedDataType:', selectedDataType);
        console.log('DataTypeConfigView - configData?.attributes:', configData?.attributes);
        if (selectedDataType?.value === 'OBJECT' && configData?.attributes) {
            console.log('DataTypeConfigView - Setting objectAttributes from configData:', configData.attributes);
            setObjectAttributes(configData.attributes);
        } else if (selectedDataType?.value === 'OBJECT') {
            console.log('DataTypeConfigView - OBJECT type but no attributes in configData, setting empty array');
            setObjectAttributes([]);
        }
        if (selectedDataType?.value === 'ENUM' && configData?.enumValues) {
            setEnumValues(configData.enumValues);
        }
    }, [configData?.attributes, configData?.enumValues, selectedDataType?.value]);

    // Initialize local state from configData when component mounts or data type changes
    useEffect(() => {
       
        if (selectedDataType?.value === 'OBJECT') {
            console.log('DataTypeConfigView - Setting objectAttributes from initialization:', configData?.attributes || []);
            setObjectAttributes(configData?.attributes || []);
        }
        if (selectedDataType?.value === 'ENUM') {
            setEnumValues(configData?.enumValues || []);
        }
    }, [selectedDataType?.value, configData?.attributes, configData?.enumValues]);

    // Handle setting existing values when dynamicKeyValueAttributeOptions are loaded
    useEffect(() => {
        if (selectedDataType?.value === 'COLLECTION_OBJECT' && hasDynamicOptions && dynamicKeyValueAttributeOptions.length > 0 && configData) {
            console.log('Setting existing values from configData with dynamic options:', configData);

            if (configData.keyAttribute) {
                const keyAttr = dynamicKeyValueAttributeOptions.find(opt => opt.value === configData.keyAttribute);
                console.log('Setting keyAttributeKeyValue with dynamic options:', keyAttr);
                setKeyAttributeKeyValue(keyAttr || "");
            }
            if (configData.valueAttribute) {
                const valueAttr = dynamicKeyValueAttributeOptions.find(opt => opt.value === configData.valueAttribute);
                console.log('Setting valueAttributeKeyValue with dynamic options:', valueAttr);
                setValueAttributeKeyValue(valueAttr || "");
            }
            if (configData.foreignKey) {
                const foreignKey = dynamicKeyValueAttributeOptions.find(opt => opt.value === configData.foreignKey);
                console.log('Setting foreignKeyKeyValue with dynamic options:', foreignKey);
                setForeignKeyKeyValue(foreignKey || "");
            }
        }
    }, [hasDynamicOptions, dynamicKeyValueAttributeOptions, selectedDataType?.value, configData]);

    // Handle setting existing mapping table values when dynamicMappingTableAttributeOptions are loaded
    useEffect(() => {
        if (selectedDataType?.value === 'COLLECTION_OBJECT' && hasDynamicMappingOptions && dynamicMappingTableAttributeOptions.length > 0 && configData) {
            console.log('Setting existing mapping values from configData with dynamic options:', configData);

            if (configData.currentTableParameterReferenceId) {
                const sourceAttr = dynamicMappingTableAttributeOptions.find(opt => opt.value === configData.currentTableParameterReferenceId);
                console.log('Setting currentTableParameterReferenceId with dynamic options:', sourceAttr);
                // Note: We don't set state here as it's handled by the configData directly
            }
            if (configData.referenceTableParameterReferenceId) {
                const targetAttr = dynamicMappingTableAttributeOptions.find(opt => opt.value === configData.referenceTableParameterReferenceId);
                console.log('Setting referenceTableParameterReferenceId with dynamic options:', targetAttr);
                // Note: We don't set state here as it's handled by the configData directly
            }
        }
    }, [hasDynamicMappingOptions, dynamicMappingTableAttributeOptions, selectedDataType?.value, configData]);

    // Handle setting reference table value when referenceTableOptions are loaded
    useEffect(() => {
        if (selectedDataType?.value === 'COLLECTION_OBJECT' && referenceTableOptions.length > 0 && configData?.referenceTable) {
            console.log('Setting reference table value from configData with loaded options:', configData);
            const refTable = referenceTableOptions.find(opt => opt.value === configData.referenceTable);
            console.log('Setting referenceTableKeyValue with loaded options:', refTable);
            setReferenceTableKeyValue(refTable || "");
        }
    }, [referenceTableOptions, selectedDataType?.value, configData?.referenceTable]);

    // Debug useEffect to monitor dynamic options changes
    useEffect(() => {
        console.log('dynamicKeyValueAttributeOptions changed:', dynamicKeyValueAttributeOptions);
        console.log('dynamicKeyValueAttributeOptions length:', dynamicKeyValueAttributeOptions.length);
        console.log('dynamicKeyValueAttributeOptions type:', typeof dynamicKeyValueAttributeOptions);
        console.log('Is dynamicKeyValueAttributeOptions array?', Array.isArray(dynamicKeyValueAttributeOptions));
    }, [dynamicKeyValueAttributeOptions]);

    // Update key/value/foreign key values when dynamic options change
    useEffect(() => {
        if (hasDynamicOptions && dynamicKeyValueAttributeOptions.length > 0 && configData) {
            console.log('Updating values for dynamic options with configData:', configData);
            
            // Update keyAttributeKeyValue if it exists in configData
            if (configData.keyAttribute) {
                const keyAttr = dynamicKeyValueAttributeOptions.find(opt => opt.value === configData.keyAttribute);
                console.log('Updating keyAttributeKeyValue for dynamic options:', keyAttr);
                setKeyAttributeKeyValue(keyAttr || "");
            }
            
            // Update valueAttributeKeyValue if it exists in configData
            if (configData.valueAttribute) {
                const valueAttr = dynamicKeyValueAttributeOptions.find(opt => opt.value === configData.valueAttribute);
                console.log('Updating valueAttributeKeyValue for dynamic options:', valueAttr);
                setValueAttributeKeyValue(valueAttr || "");
            }
            
            // Update foreignKeyKeyValue if it exists in configData
            if (configData.foreignKey) {
                const foreignKey = dynamicKeyValueAttributeOptions.find(opt => opt.value === configData.foreignKey);
                console.log('Updating foreignKeyKeyValue for dynamic options:', foreignKey);
                setForeignKeyKeyValue(foreignKey || "");
            }
        }
    }, [hasDynamicOptions, dynamicKeyValueAttributeOptions, configData]);

    // Update mapping table attribute values when dynamic mapping options change
    useEffect(() => {
        if (dynamicMappingTableAttributeOptions.length > 0 && configData) {
            console.log('Updating mapping table values for dynamic options with configData:', configData);
            
            // Note: Mapping table attributes are handled directly in configData
            // The Select components use configData values directly, so they should update automatically
            console.log('Mapping table attributes should update automatically from configData');
        }
    }, [dynamicMappingTableAttributeOptions]);

    // removed forceRender tracking to avoid unnecessary re-renders

    useEffect(() => {
        console.log('dynamicMappingTableAttributeOptions changed:', dynamicMappingTableAttributeOptions);
    }, [dynamicMappingTableAttributeOptions]);

    useEffect(() => {
        console.log('hasDynamicOptions changed:', hasDynamicOptions);
    }, [hasDynamicOptions]);

    useEffect(() => {
        console.log('hasDynamicMappingOptions changed:', hasDynamicMappingOptions);
    }, [hasDynamicMappingOptions]);

    // Clear dependent values when reference table changes
    useEffect(() => {
        if (selectedDataType?.value === 'COLLECTION_OBJECT' && configData?.referenceTable) {
            console.log('Reference table changed, clearing dependent values');
            if (onConfigChange) {
                onConfigChange('keyAttribute', '');
                onConfigChange('valueAttribute', '');
                onConfigChange('foreignKey', '');
                onConfigChange('keyAttributeName', '');
                onConfigChange('valueAttributeName', '');
                onConfigChange('foreignKeyName', '');
            }
            setKeyAttributeKeyValue("");
            setValueAttributeKeyValue("");
            setForeignKeyKeyValue("");
        }
    }, [configData?.referenceTable, selectedDataType?.value]);

    const handleFieldChange = (fieldName, value, fieldType = 'text') => {
        if (onConfigChange) {
            onConfigChange(fieldName, value, fieldType);
        }
    };

    const handleObjectAttributesChange = (updatedConfig) => {
        const attributes = updatedConfig?.attributes || [];
        setObjectAttributes(attributes);
        if (onConfigChange) {
            onConfigChange('attributes', attributes);
        }
    };

    const handleEnumValuesChange = (updatedConfig) => {
        const enumValues = updatedConfig?.enumValues || [];
        setEnumValues(enumValues);
        if (onConfigChange) {
            onConfigChange('enumValues', enumValues);
        }
    };

    const handleCollectionObjectChange = (field, value) => {
        if (onConfigChange) {
            onConfigChange(field, value);
        }
    };

    // Function to get key-value attribute options for a table
    const getKeyValueAttributeOptionsList = (tableId) => {
        if (!tableId) {
            console.log('getKeyValueAttributeOptionsList: tableId is empty or undefined');
            return;
        }
        
        console.log('getKeyValueAttributeOptionsList called with tableId:', tableId);
        console.log('Current dynamicKeyValueAttributeOptions before API call:', dynamicKeyValueAttributeOptions);
        
        const { DYNAMIC_TABLE_ATTRIBUTE_LIST_BY_TABLE_ID_GET } = require('../../api/ApiConst');
        let keyValueAttributeListUrl = DYNAMIC_TABLE_ATTRIBUTE_LIST_BY_TABLE_ID_GET.replaceAll("{dynamic-table-id}", tableId);
        
        console.log('Calling API for table attributes:', keyValueAttributeListUrl);
        
        ApiCaller.callPostApi(keyValueAttributeListUrl, tableId, {}, (response) => {
            console.log('API response received:', response);
            console.log('Response type:', typeof response);
            console.log('Response.data:', response?.data);
            console.log('Response.data type:', typeof response?.data);
            console.log('Is response.data array?', Array.isArray(response?.data));
            
            if (response && response.data && Array.isArray(response.data)) {
                console.log('Response.data length:', response.data.length);
                console.log('First item in response.data:', response.data[0]);
                
                const options = response.data.map((data, i) => {
                    const option = { 
                        label: data.name, 
                        value: data.id 
                    };
                    console.log(`Mapping item ${i}:`, data, '→', option);
                    return option;
                });
                
                console.log('Final options array:', options);
                console.log('Setting dynamicKeyValueAttributeOptions with length:', options.length);
                
                if (setDynamicKeyValueAttributeOptions) {
                    setDynamicKeyValueAttributeOptions(options);
                }
                if (setHasDynamicOptions) {
                    setHasDynamicOptions(true);
                }
                console.log('dynamicKeyValueAttributeOptions state updated, length:', options.length);
                console.log('hasDynamicOptions set to true');
                
                // Avoid forced remounts; Selects update from state changes
                
                // Set selected values from configData after options are loaded
                console.log('Setting selected values from configData after options loaded');
                if (configData) {
                    if (configData.keyAttribute) {
                        const keyAttr = options.find(opt => opt.value === configData.keyAttribute);
                        console.log('Setting keyAttributeKeyValue from configData:', keyAttr);
                        setKeyAttributeKeyValue(keyAttr || "");
                    }
                    if (configData.valueAttribute) {
                        const valueAttr = options.find(opt => opt.value === configData.valueAttribute);
                        console.log('Setting valueAttributeKeyValue from configData:', valueAttr);
                        setValueAttributeKeyValue(valueAttr || "");
                    }
                    if (configData.foreignKey) {
                        const foreignKey = options.find(opt => opt.value === configData.foreignKey);
                        console.log('Setting foreignKeyKeyValue from configData:', foreignKey);
                        setForeignKeyKeyValue(foreignKey || "");
                    }
                }
            } else {
                console.log('Invalid response format or no data:', response);
                console.log('Setting dynamicKeyValueAttributeOptions to empty array');
                setDynamicKeyValueAttributeOptions([]);
                setHasDynamicOptions(false);
            }
        }, (error) => {
            console.error('Error loading key-value attribute options:', error);
            console.log('Setting dynamicKeyValueAttributeOptions to empty array due to error');
            setDynamicKeyValueAttributeOptions([]);
            setHasDynamicOptions(false);
        });
    };

    // Function to get mapping table attribute options
    const getMappingTableKeyValueOptionsList = (tableId) => {
        if (!tableId) return;
        
        console.log('getMappingTableKeyValueOptionsList called with tableId:', tableId);
        
        const { DYNAMIC_TABLE_ATTRIBUTE_LIST_BY_TABLE_ID_GET } = require('../../api/ApiConst');
        let keyValueAttributeListUrl = DYNAMIC_TABLE_ATTRIBUTE_LIST_BY_TABLE_ID_GET.replaceAll("{dynamic-table-id}", tableId);
        
        console.log('Calling API for mapping table attributes:', keyValueAttributeListUrl);
        
        ApiCaller.callPostApi(keyValueAttributeListUrl, tableId, {}, (response) => {
            console.log('API response for mapping table attributes:', response);
            const options = response.data.map((data, i) => ({ 
                label: data.name, 
                value: data.id 
            }));
            console.log('Setting dynamicMappingTableAttributeOptions:', options);
            setDynamicMappingTableAttributeOptions(options);
            setHasDynamicMappingOptions(true);
            console.log('hasDynamicMappingOptions set to true');
            
            // Set selected mapping values from configData after options are loaded
            console.log('Setting selected mapping values from configData after options loaded');
            if (configData) {
                if (configData.currentTableParameterReferenceId) {
                    const sourceAttr = options.find(opt => opt.value === configData.currentTableParameterReferenceId);
                    console.log('Found currentTableParameterReferenceId in options:', sourceAttr);
                }
                if (configData.referenceTableParameterReferenceId) {
                    const targetAttr = options.find(opt => opt.value === configData.referenceTableParameterReferenceId);
                    console.log('Found referenceTableParameterReferenceId in options:', targetAttr);
                }
            }
        }, (error) => {
            console.error('Error loading mapping table attribute options:', error);
            setDynamicMappingTableAttributeOptions([]);
            setHasDynamicMappingOptions(false);
        });
    };

    const renderConfigField = (field, index) => {
        const fieldName = field.name;
        const fieldType = field.type;
        const title = field.title;
        const fieldValue = configData[fieldName] || '';
        const fieldDescription = field.description;

        // Prepare options for specific field types
        let options = undefined;
        if (fieldType === 'ENUM' && field.config && field.config.enumValues) {
            try {
                const enumValues = typeof field.config.enumValues === 'string'
                    ? JSON.parse(field.config.enumValues)
                    : field.config.enumValues;
                options = enumValues.map(value => ({ label: value, value }));
            } catch (error) {
                console.error('Error parsing enum values:', error);
            }
        } else if (fieldType === 'COLLECTION_OBJECT') {
            options = referenceTableOptions;
        }

        return (
            <Col md={6} key={index} className="mb-3">
                <Form.Group>
                    <Form.Label>
                        {title}
                    </Form.Label>
                    <DataTypeFactory
                        type={fieldType}
                        value={fieldValue}
                        onChange={(value) => handleFieldChange(fieldName, value)}
                        mode="edit"
                        config={field.config || {}}
                        options={options}
                        field={{ name: fieldName, type: fieldType }}
                    />
                </Form.Group>
            </Col>
        );
    };

    if (!selectedDataType || !selectedDataType.value) {
        return null;
    }

    // Use DataTypeFactory for OBJECT and ENUM types
    if (selectedDataType.value === 'OBJECT' || selectedDataType.value === 'ENUM') {
        const fieldConfig = selectedDataType.value === 'OBJECT' 
            ? { editAttributes: true }
            : { editEnumValues: true };
            
        const fieldConfigData = selectedDataType.value === 'OBJECT'
            ? { attributes: configData?.attributes || objectAttributes }
            : { enumValues: configData?.enumValues || enumValues };
            
        const onChangeHandler = selectedDataType.value === 'OBJECT'
            ? handleObjectAttributesChange
            : handleEnumValuesChange;
            
        return (
            <DataTypeFactory
                type={selectedDataType.value}
                mode="edit"
                field={fieldConfig}
                config={fieldConfigData}
                onChange={onChangeHandler}
            />
        );
    }

    // Special handling for COLLECTION_OBJECT type
    if (selectedDataType.value === 'COLLECTION_OBJECT') {
        return (
            <Card className="mt-3">
                <Card.Body>
                    <Row>
                        <Col md={12}>
                            <div className="mb-3">
                                <label>Reference Table *</label>
                                <Select
                                    options={referenceTableOptions}
                                    value={referenceTableKeyValue}
                                    onChange={(e) => {
                                        console.log('Reference Table onChange called with:', e);
                                        console.log('Selected table value:', e?.value);
                                        console.log('Selected table label:', e?.label);
                                        
                                        const previousTableId = configData?.referenceTable;
                                        const newTableId = e?.value;
                                        
                                        setReferenceTableKeyValue(e);
                                        
                                        // Only clear dependent values if the table has actually changed
                                        if (previousTableId !== newTableId) {
                                            console.log('Table changed from', previousTableId, 'to', newTableId);
                                            
                                            // Clear dynamic options first to prevent stale data
                                            console.log('Clearing dynamicKeyValueAttributeOptions');
                                            if (setDynamicKeyValueAttributeOptions) {
                                                setDynamicKeyValueAttributeOptions([]);
                                            }
                                            if (setHasDynamicOptions) {
                                                setHasDynamicOptions(false);
                                            }
                                            console.log('hasDynamicOptions set to false');
                                            
                                            // Load key-value attribute options when reference table is selected
                                            if (newTableId) {
                                                console.log('Calling getKeyValueAttributeOptionsList with tableId:', newTableId);
                                                getKeyValueAttributeOptionsList(newTableId);
                                            } else {
                                                console.log('No table value selected, skipping API call');
                                            }
                                            
                                            // Update configData with new reference table
                                            if (onConfigChange) {
                                                console.log('Calling onConfigChange for referenceTable:', newTableId || '');
                                                onConfigChange('referenceTable', newTableId || '');
                                            }
                                        } else {
                                            console.log('Same table selected, preserving existing values');
                                            // Just update the reference table without clearing other values
                                            if (onConfigChange) {
                                                onConfigChange('referenceTable', newTableId || '');
                                            }
                                        }
                                    }}
                                    className="react-select-container"
                                    placeholder="Select Reference Table"
                                />
                            </div>
                            <div className="mb-3">
                                <Row>
                                    <Col md={6}>
                        <label>Key Attribute *</label>
                        <Select
                                            options={hasDynamicOptions ? dynamicKeyValueAttributeOptions : keyValueAttributeOptions}
                                            value={keyAttributeKeyValue}
                                            onChange={(e) => {
                                                console.log('Key Attribute onChange:', e);
                                                setKeyAttributeKeyValue(e);
                                                handleCollectionObjectChange('keyAttribute', e?.value);
                                            }}
                                            className="react-select-container"
                                            placeholder="Select Key Attribute"
                                            onMenuOpen={() => {
                                                console.log('Key Attribute menu opened');
                                                console.log('hasDynamicOptions:', hasDynamicOptions);
                                                console.log('dynamicKeyValueAttributeOptions.length:', dynamicKeyValueAttributeOptions.length);
                                                console.log('keyValueAttributeOptions.length:', keyValueAttributeOptions.length);
                                                console.log('Selected options array:', hasDynamicOptions ? dynamicKeyValueAttributeOptions : keyValueAttributeOptions);
                                                console.log('Available options:', hasDynamicOptions ? dynamicKeyValueAttributeOptions : keyValueAttributeOptions);
                                            }}
                                        />
                                        {(() => {
                                            console.log('Key Attribute Select - dynamicKeyValueAttributeOptions:', dynamicKeyValueAttributeOptions);
                                            console.log('Key Attribute Select - keyValueAttributeOptions:', keyValueAttributeOptions);
                                            console.log('Key Attribute Select - final options:', dynamicKeyValueAttributeOptions.length > 0 ? dynamicKeyValueAttributeOptions : keyValueAttributeOptions);
                                            return null;
                                        })()}
                                    </Col>
                                    <Col md={6}>
                        <label>Value Attribute *</label>
                        <Select
                                            options={hasDynamicOptions ? dynamicKeyValueAttributeOptions : keyValueAttributeOptions}
                                            value={valueAttributeKeyValue}
                                            onChange={(e) => {
                                                console.log('Value Attribute onChange:', e);
                                                setValueAttributeKeyValue(e);
                                                handleCollectionObjectChange('valueAttribute', e?.value);
                                            }}
                                            className="react-select-container"
                                            placeholder="Select Value Attribute"
                                            onMenuOpen={() => {
                                                console.log('Value Attribute menu opened');
                                                console.log('hasDynamicOptions:', hasDynamicOptions);
                                                console.log('dynamicKeyValueAttributeOptions.length:', dynamicKeyValueAttributeOptions.length);
                                                console.log('keyValueAttributeOptions.length:', keyValueAttributeOptions.length);
                                                console.log('Selected options array:', hasDynamicOptions ? dynamicKeyValueAttributeOptions : keyValueAttributeOptions);
                                                console.log('Available options:', hasDynamicOptions ? dynamicKeyValueAttributeOptions : keyValueAttributeOptions);
                                            }}
                                        />
                                        {(() => {
                                            console.log('Value Attribute Select - dynamicKeyValueAttributeOptions:', dynamicKeyValueAttributeOptions);
                                            console.log('Value Attribute Select - keyValueAttributeOptions:', keyValueAttributeOptions);
                                            console.log('Value Attribute Select - final options:', dynamicKeyValueAttributeOptions.length > 0 ? dynamicKeyValueAttributeOptions : keyValueAttributeOptions);
                                            return null;
                                        })()}
                                    </Col>
                                </Row>
                            </div>
                            <div className="mb-3">
                                <label>Foreign Key *</label>
                                <Select
                                    options={hasDynamicOptions ? dynamicKeyValueAttributeOptions : keyValueAttributeOptions}
                                    value={foreignKeyKeyValue}
                                    onChange={(e) => {
                                        console.log('Foreign Key onChange:', e);
                                        setForeignKeyKeyValue(e);
                                        handleCollectionObjectChange('foreignKey', e?.value);
                                    }}
                                    className="react-select-container"
                                    placeholder="Select Foreign Key"
                                />
                                {(() => {
                                    console.log('Foreign Key Select - dynamicKeyValueAttributeOptions:', dynamicKeyValueAttributeOptions);
                                    console.log('Foreign Key Select - final options:', dynamicKeyValueAttributeOptions.length > 0 ? dynamicKeyValueAttributeOptions : keyValueAttributeOptions);
                                    return null;
                                })()}
                            </div>
                            <div className="mb-3">
                                <Row>
                                    <Col md={6}>
                                        <input
                                            type="checkbox"
                                            name="referenceDataColumn"
                                            id="referenceDataColumn"
                                            checked={configData.referenceDataColumn || false}
                                            onChange={(e) => handleCollectionObjectChange('referenceDataColumn', e.target.checked)}
                                        />
                                        <label className='ms-2'>Reference Data Column</label>
                                    </Col>
                                    {configData?.referenceDataColumn && (
                                        <Col md={6}>
                                            <input
                                                type="checkbox"
                                                name="referenceWithMappingTable"
                                                id="referenceWithMappingTable"
                                                checked={configData.referenceWithMappingTable || false}
                                                onChange={(e) => handleCollectionObjectChange('referenceWithMappingTable', e.target.checked)}
                                            />
                                            <label className='ms-2'>Reference With Mapping Table</label>
                                        </Col>
                                    )}
                                </Row>
                            </div>
                            {(configData?.referenceWithMappingTable && configData.referenceDataColumn) && (
                                <>
                                    <div className="mb-3">
                                        <label>Mapping Table *</label>
                                        <Select
                                            options={referenceTableOptions}
                                            value={configData?.mappingTableId ? { value: configData.mappingTableId, label: configData.mappingTableName || 'Loading...' } : null}
                                            onChange={(e) => {
                                                const previousMappingTableId = configData?.mappingTableId;
                                                const newMappingTableId = e?.value;
                                                
                                                // Only clear dependent values if the mapping table has actually changed
                                                if (previousMappingTableId !== newMappingTableId) {
                                                    console.log('Mapping table changed from', previousMappingTableId, 'to', newMappingTableId);
                                                    
                                                    // Clear dynamic mapping options first to prevent stale data
                                                    setDynamicMappingTableAttributeOptions([]);
                                                    setHasDynamicMappingOptions(false);
                                                    console.log('hasDynamicMappingOptions set to false');
                                                    
                                                    // Load mapping table attribute options when mapping table is selected
                                                    if (newMappingTableId) {
                                                        getMappingTableKeyValueOptionsList(newMappingTableId);
                                                    }
                                                    
                                                    // Update configData with new mapping table
                                                    if (onConfigChange) {
                                                        onConfigChange('mappingTableId', newMappingTableId || '');
                                                        onConfigChange('mappingTableName', e?.label || '');
                                                    }
                                                } else {
                                                    console.log('Same mapping table selected, preserving existing values');
                                                    // Just update the mapping table without clearing other values
                                                    if (onConfigChange) {
                                                        onConfigChange('mappingTableId', newMappingTableId || '');
                                                        onConfigChange('mappingTableName', e?.label || '');
                                                    }
                                                }
                                            }}
                                            className="react-select-container"
                                            placeholder="Select Mapping Table"
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <Row>
                                            <Col md={6}>
                                                <label>Mapping Source Attribute *</label>
                                                <Select
                                                    options={hasDynamicMappingOptions ? dynamicMappingTableAttributeOptions : mappingTableAttributeKeyValueOptions}
                                                    value={configData?.currentTableParameterReferenceId ? { value: configData.currentTableParameterReferenceId, label: configData.currentTableParameterReferenceName || 'Loading...' } : null}
                                                    onChange={(e) => {
                                                        handleCollectionObjectChange('currentTableParameterReferenceId', e?.value);
                                                        handleCollectionObjectChange('currentTableParameterReferenceName', e?.label);
                                                    }}
                                                    className="react-select-container"
                                                    placeholder="Select Mapping Source Attribute"
                                                />
                                                {(() => {
                                                    console.log('Mapping Source options:', hasDynamicMappingOptions ? dynamicMappingTableAttributeOptions : mappingTableAttributeKeyValueOptions);
                                                    return null;
                                                })()}
                                            </Col>
                                            <Col md={6}>
                                                <label>Mapping Target Attribute *</label>
                                                <Select
                                                    options={hasDynamicMappingOptions ? dynamicMappingTableAttributeOptions : mappingTableAttributeKeyValueOptions}
                                                    value={configData?.referenceTableParameterReferenceId ? { value: configData.referenceTableParameterReferenceId, label: configData.referenceTableParameterReferenceName || 'Loading...' } : null}
                                                    onChange={(e) => {
                                                        handleCollectionObjectChange('referenceTableParameterReferenceId', e?.value);
                                                        handleCollectionObjectChange('referenceTableParameterReferenceName', e?.label);
                                                    }}
                                                    className="react-select-container"
                                                    placeholder="Select Mapping Target Attribute"
                                                />
                                                {(() => {
                                                    console.log('Mapping Target options:', hasDynamicMappingOptions ? dynamicMappingTableAttributeOptions : mappingTableAttributeKeyValueOptions);
                                                    return null;
                                                })()}
                                            </Col>
                                        </Row>
                                    </div>
                                </>
                            )}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        );
    }

    if (isLoading) {
        return (
            <div className="alert alert-info">
                <strong>Loading configuration for {selectedDataType.label}...</strong>
            </div>
        );
    }

    if (configFields.length === 0) {
        return (
            <div className="alert alert-warning">
                <strong>No configuration fields found for {selectedDataType.label}</strong>
                <p className="mb-0 mt-2">
                    This data type doesn't have specific configuration options defined in the system.
                    The parameter will be created with default settings.
                </p>
            </div>
        );
    }

    return (
        <Card className="mt-3">
            <Card.Body>
                <Row>
                    {configFields.map((field, index) => renderConfigField(field, index))}
                </Row>
            </Card.Body>
        </Card>
    );
};

// Synchronous version that returns cached data if available
export const getDataTypesSync = (configDataTypes) => {
    if (!configDataTypes || typeof configDataTypes !== 'object') {
        return [];
    }

    return Object.keys(configDataTypes).map(type => ({
        label: type,
        value: type
    }));
};

const DataTypeConfig = ({
    configDataTypes,
    setConfigDataTypes,
    isLoading,
    setIsLoading
}) => {
    const [selectedDataType, setSelectedDataType] = useState(null);

    // Fetch config data types from API
    useEffect(() => {
        if (!configDataTypes || Object.keys(configDataTypes).length === 0) {
            fetchConfigDataTypes();
        }
    }, []);

    const fetchConfigDataTypes = () => {
        setIsLoading(true);
        ApiCaller.callGetApi(
            GET_CONFIG_DATA_TYPES,
            {},
            (response) => {
                setConfigDataTypes(response.data || {});
                setIsLoading(false);
            },
            (error) => {
                console.error("Error fetching config data types:", error);
                setIsLoading(false);
            }
        );
    };

    // Handle data type selection
    const handleDataTypeSelection = (selected) => {
        setSelectedDataType(selected);
        console.log('Selected data type:', selected);
    };

    // Get dropdown options using the getDataTypes function
    const dropdownOptions = getDataTypesSync(configDataTypes);

    return (
        <div className="container mt-4">
            <Card>
                <Card.Header>
                    <h4>Data Type Selection</h4>
                    <p className="mb-0 text-muted">
                        Select a data type from the dropdown list
                    </p>
                </Card.Header>
                <Card.Body>
                    <div className="mb-3">
                        <Button variant="info" onClick={fetchConfigDataTypes} className="me-2">
                            Fetch Data Types
                        </Button>
                        <small className="text-muted">
                            Click to fetch the latest data types from API
                        </small>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-6">
                            <label>Select Data Type</label>
                            <Select
                                options={dropdownOptions}
                                value={selectedDataType}
                                onChange={handleDataTypeSelection}
                                className="react-select-container"
                                isLoading={isLoading}
                                placeholder="Select Data Type"
                            />
                        </div>
                    </div>

                    {selectedDataType && (
                        <div className="alert alert-info">
                            <h6>Selected Data Type:</h6>
                            <p><strong>Type:</strong> {selectedDataType.label}</p>
                            <p><strong>Value:</strong> {selectedDataType.value}</p>
                        </div>
                    )}

                    <div className="row">
                        <div className="col-md-6">
                            <h6>Available Data Types:</h6>
                            <pre className="bg-light p-2 rounded small">
                                {JSON.stringify(Object.keys(configDataTypes), null, 2)}
                            </pre>
                        </div>
                        <div className="col-md-6">
                            <h6>Dropdown Options:</h6>
                            <pre className="bg-light p-2 rounded small">
                                {JSON.stringify(dropdownOptions, null, 2)}
                            </pre>
                        </div>
                    </div>

                    {/* Display raw API data */}
                    <div className="mt-4">
                        <h6>Raw API Data:</h6>
                        <pre className="bg-light p-2 rounded small">
                            {JSON.stringify(configDataTypes, null, 2)}
                        </pre>
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
};

export default DataTypeConfig; 